import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TOP_PROGRESS } from '@/components/layout/top-progress-store';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { useSocketMessage } from '../socket/useSocket';
import type { AgentDraft, UpsertAgentDraftRequest, WsServerMessage } from '@/lib/types';

function upsertDraftInList(drafts: AgentDraft[], draft: AgentDraft): AgentDraft[] {
  const idx = drafts.findIndex((d) => d.id === draft.id);
  if (idx === -1) return [draft, ...drafts].sort((a, b) => b.updatedAt - a.updatedAt);
  const next = [...drafts];
  next[idx] = draft;
  return next.sort((a, b) => b.updatedAt - a.updatedAt);
}

export function useAgentDrafts() {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  const key = useMemo(() => [...queryKeys.agentDrafts(orgKey)], [orgKey]);

  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (msg.type === 'agent-draft:created' || msg.type === 'agent-draft:updated') {
          if (msg.draft.organizationId !== orgKey) return;
          queryClient.setQueryData<AgentDraft[]>(key, (old) => {
            const list = old ?? [];
            return upsertDraftInList(list, msg.draft);
          });
        } else if (msg.type === 'agent-draft:deleted') {
          queryClient.setQueryData<AgentDraft[]>(key, (old) =>
            old ? old.filter((d) => d.id !== msg.draftId) : old,
          );
        }
      },
      [queryClient, orgKey, key],
    ),
  );

  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await api.listAgentDrafts();
      return res.drafts;
    },
  });
}

export function useUpsertAgentDraft() {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  const key = useMemo(() => [...queryKeys.agentDrafts(orgKey)], [orgKey]);

  return useMutation({
    // Autosave owns a local footer indicator; keep the global top bar idle.
    meta: { [TOP_PROGRESS]: false },
    mutationFn: (body: UpsertAgentDraftRequest) => api.upsertAgentDraft(body),
    onSuccess: (res) => {
      if (res.draft.organizationId !== orgKey) return;
      queryClient.setQueryData<AgentDraft[]>(key, (old) => upsertDraftInList(old ?? [], res.draft));
    },
  });
}

export function useDeleteAgentDraft() {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  const key = useMemo(() => [...queryKeys.agentDrafts(orgKey)], [orgKey]);

  return useMutation({
    meta: { [TOP_PROGRESS]: false },
    mutationFn: (id: string) => api.deleteAgentDraft(id),
    onSuccess: (_data, id) => {
      queryClient.setQueryData<AgentDraft[]>(key, (old) =>
        old ? old.filter((d) => d.id !== id) : old,
      );
    },
  });
}
